import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { useClient } from "../../../graphql/hooks/useClient";
import { useClientStatistics } from "../../../graphql/hooks/client/useClientStatistics";
import TitleSmall from "../../atoms/typography/TitleSmall";
import { theme } from "../../../theme";
import { getAmlStatusBadge } from "../../molecules/ClientCardHeader";
import styled from "styled-components";
import { useClientCreditData } from "../../../graphql/hooks/client/useClientCreditData";
import CreditRatingBadge from "../../atoms/CreditRatingBadge";
import { HorizontalSpacer } from "../../atoms/Spacers";
import Body from "../../atoms/typography/Body";
import Card from "../../atoms/Card";
import InCardSectionTitle from "../../molecules/InCardSectionTitle";
import { Col, Row } from "react-grid-system";
import ClientOrgInfo from "../../molecules/client/ClientOrgInfo";
import Subheading from "../../atoms/typography/Subheading";
import { useInvoiceUploadUnits } from "../../../graphql/hooks/documentHandler/useInvoiceUploadUnits";
import { ACTIVE_STATUSES, DONE_STATUSES } from "../DocumentHandler";
import BodySmall from "../../atoms/typography/BodySmall";
import TableRow from "../../atoms/TableRow";
import dayjs from "dayjs";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import { useClientInvoices } from "../../../graphql/hooks/invoices/useClientInvoices";
import { getInvoiceListStatusBadge } from "../../../utils/invoicesHelpers";
import { formatPrice, getCurrencySymbol } from "../../../utils/formatters";
import TextLinkButton from "../../atoms/buttons/TextLinkButton";
import { ChevronSmallRightIcon } from "../../atoms/icons/ChevronIcons";
import { useNavigate } from "react-router-dom";
import CreditScoreOverviewSmall from "../../molecules/assessment/CreditScoreOverviewSmall";
import DonutStatSection from "../../molecules/assessment/DonutStatSection";
import Small from "../../atoms/typography/Small";
import CircleIcon from "../../atoms/CircleIcon";
import { PaymentSuccessIcon } from "../../atoms/icons/PaymentSuccessIcon";
import CopyForDebuggingButton from "../../molecules/CopyForDebuggingButton";
import { UploadUnitStatusBadge } from "../../../utils/documentHandlerHelpers";

interface Props {
  clientIdentifier: string;
}

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StatContainer = styled.div`
  display: flex;
  gap: 8px;
  border-radius: 8px;
  background-color: ${theme.colors.bgSubtle};
  align-items: center;
  padding: 24px;
  width: 240px;
`;

const ClientOverviewBeta: FC<Props> = ({ clientIdentifier }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient(clientIdentifier);
  const { clientStatistics: clientStatistics } =
    useClientStatistics(clientIdentifier);
  const { clientCreditData } = useClientCreditData(clientIdentifier);
  const { uploadUnits } = useInvoiceUploadUnits(
    clientIdentifier,
    [...ACTIVE_STATUSES, ...DONE_STATUSES],
    0,
    3,
    "CREATED_AT",
    "DESC",
  );
  const { invoices } = useClientInvoices(clientIdentifier, {
    sortBy: "CREATED_AT",
    sortDirection: "DESC",
    skip: 0,
    take: 5,
  });

  const onClickGoToClientInvoices = () => {
    navigate(`/client/${clientIdentifier}/invoicesNew`);
  };

  const financialLimit = clientStatistics?.limits?.financialLimit;
  const financialTotal = financialLimit?.limit ?? 0;
  const financialUsed = financialLimit?.used ?? 0;
  const financialRemaining = Math.max(financialTotal - financialUsed, 0);

  const recipientLimit = clientStatistics?.limits?.recipientLimit;
  const recipientTotal = recipientLimit?.limit ?? 0;
  const recipientUsed = recipientLimit?.used ?? 0;
  const recipientRemaining = Math.max(recipientTotal - recipientUsed, 0);

  const repurchaseLimit = clientStatistics?.limits?.repurchaseLimit;
  const repurchaseTotal = repurchaseLimit?.limit ?? 0;
  const repurchaseUsed = repurchaseLimit?.used ?? 0;
  const repurchaseRemaining = Math.max(repurchaseTotal - repurchaseUsed, 0);

  return (
    <div>
      <HeaderSection>
        <TitleSmall color={theme.colors.primary}>{client?.name}</TitleSmall>
        {getAmlStatusBadge(
          client?.riskScore?.current?.totalScore,
          client?.riskScore?.current?.riskScoreClass,
          client?.riskScore?.currentManualRiskScore !== null,
        )}
        {clientCreditData?.creditData?.creditRating?.legacyRating && (
          <CreditRatingBadge
            rating={clientCreditData.creditData.creditRating.legacyRating}
          />
        )}
      </HeaderSection>
      <HorizontalSpacer spacing={0.5} />
      {client?.identifier && (
        <CopyForDebuggingButton
          textValue={t("common.organisationNumber") + ": " + client?.identifier}
          copyValue={client?.identifier}
        />
      )}

      <HorizontalSpacer spacing={2} />
      <Card spacing={2}>
        <Subheading color={theme.colors.primary}>
          Information om bolaget
        </Subheading>
        <HorizontalSpacer spacing={1} />
        <div>
          <Row>
            <Col lg={3}>
              <InCardSectionTitle
                title={t("clientCard.aml")}
                status={getAmlStatusBadge(
                  client?.riskScore?.current?.totalScore,
                  client?.riskScore?.current?.riskScoreClass,
                  client?.riskScore?.currentManualRiskScore !== null,
                )}
              />
            </Col>
            <Col lg={3}>
              <InCardSectionTitle
                title={t("clientCard.integration")}
                content={client?.settings?.accounting?.accountingSystem ?? "-"}
              />
            </Col>
            <Col lg={3}>
              <InCardSectionTitle
                title={t("clientCard.autobook")}
                content={
                  client?.settings?.accounting?.autobookActive
                    ? t("common.active")
                    : t("common.inactive")
                }
              />
            </Col>
          </Row>
        </div>
      </Card>
      <HorizontalSpacer spacing={2} />
      <Card spacing={2}>
        <Row>
          <Col lg={6}>
            <CreditScoreOverviewSmall
              clientIdentifier={clientIdentifier}
              expandable
              hideClientCardLinkButton
            />
          </Col>
          <Col lg={6}>
            <ClientOrgInfo clientIdentifier={clientIdentifier} />
          </Col>
        </Row>
      </Card>
      <HorizontalSpacer spacing={2} />

      <Card spacing={2}>
        <Subheading color={theme.colors.primary}>
          Senaste dokumenten & fakturorna
        </Subheading>
        <HorizontalSpacer spacing={0.5} />
        <TableHeader>
          <Col lg={3}>
            <TableHeaderText>Mottagare/Dokument</TableHeaderText>
          </Col>
          <Col lg={2}>
            <TableHeaderText>Status</TableHeaderText>
          </Col>
          <Col lg={2}>
            <TableHeaderText>Skapat</TableHeaderText>
          </Col>
          <Col lg={2}>
            <TableHeaderText>Belopp</TableHeaderText>
          </Col>
          <Col lg={2}>
            <TableHeaderText>Förfallodatum</TableHeaderText>
          </Col>
        </TableHeader>
        {uploadUnits?.items?.map((uploadUnit) => (
          <TableRow key={uploadUnit.id} clickable={false}>
            <Col lg={3}>
              <BodySmall>
                {uploadUnit?.invoiceDocuments?.length} dokument
              </BodySmall>
            </Col>
            <Col lg={2}>
              <UploadUnitStatusBadge status={uploadUnit.status} small />
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(uploadUnit?.createdAt).format("YYYY-MM-DD HH:mm")}
              </BodySmall>
            </Col>
          </TableRow>
        ))}
        {invoices?.items?.map((invoice) => (
          <TableRow key={invoice.id} clickable={false}>
            <Col lg={3}>
              <BodySmall>
                {invoice?.invoiceRecipient?.client?.name ??
                  invoice?.invoiceRecipient?.name}
              </BodySmall>
            </Col>
            <Col lg={2}>
              {getInvoiceListStatusBadge(
                invoice?.state?.processStatus ?? "UNKNOWN",
              )}
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(invoice?.createdAt).format("YYYY-MM-DD HH:mm")}
              </BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {formatPrice(invoice.totalAmountWithVat)}{" "}
                {getCurrencySymbol(invoice?.currencyCode)}
              </BodySmall>
            </Col>
            <Col lg={2}>
              <BodySmall>
                {dayjs(invoice?.dueDate).format("YYYY-MM-DD")}
              </BodySmall>
            </Col>
          </TableRow>
        ))}
        <HorizontalSpacer spacing={1} />
        <Row nogutter justify="end">
          <TextLinkButton
            title="Visa fler"
            icon={<ChevronSmallRightIcon />}
            onClick={onClickGoToClientInvoices}
          />
        </Row>
      </Card>

      <HorizontalSpacer spacing={2} />

      <Card spacing={2}>
        <div style={{ display: "flex", gap: 24, marginBottom: 16 }}>
          <StatContainer>
            <CircleIcon
              icon={<PaymentSuccessIcon color={theme.colors.white} />}
              type="custom"
              color={theme.colors.primary}
            />
            <div>
              <Small fontweight={400} color={theme.colors.textSecondary}>
                Exponering
              </Small>
              <Body fontweight="500">
                {formatPrice(
                  clientStatistics?.invoicesStatistics?.activeInvoicesValue,
                )}{" "}
                kr
              </Body>
            </div>
          </StatContainer>
          <StatContainer>
            <CircleIcon
              icon={<PaymentSuccessIcon color={theme.colors.white} />}
              type="custom"
              color={theme.colors.primary}
            />
            <div>
              <Small fontweight={400} color={theme.colors.textSecondary}>
                Skuldsaldo
              </Small>
              <Body fontweight="500">
                {formatPrice(
                  clientStatistics?.invoicesStatistics?.depositBalance,
                )}{" "}
                kr
              </Body>
            </div>
          </StatContainer>
          <StatContainer>
            <CircleIcon
              icon={<PaymentSuccessIcon color={theme.colors.white} />}
              type="custom"
              color={theme.colors.primary}
            />
            <div>
              <Small fontweight={400} color={theme.colors.textSecondary}>
                Spärrkonto
              </Small>
              <Body fontweight="500">
                {formatPrice(clientStatistics?.invoicesStatistics?.escrowValue)}{" "}
                kr
              </Body>
            </div>
          </StatContainer>
        </div>
        <Row>
          <Col lg={6} xl={3}>
            <DonutStatSection
              data={[
                {
                  id: "1",
                  titleKey: "Använt utrymme",
                  percentage:
                    financialTotal > 0
                      ? (financialUsed / financialTotal) * 100
                      : 0,
                  valueText: `${formatPrice(financialUsed, 0)} kr`,
                },
                {
                  id: "2",
                  titleKey: "Återstående limit",
                  percentage:
                    financialTotal > 0
                      ? (financialRemaining / financialTotal) * 100
                      : 0,
                  valueText: `${formatPrice(financialRemaining, 0)} kr`,
                },
              ]}
              colorType="turqoise"
              title={`Finansieringslimit - ${formatPrice(financialTotal, 0)} kr`}
            />
          </Col>
          <Col lg={6} xl={3}>
            <DonutStatSection
              data={[
                {
                  id: "1",
                  titleKey: "Använt utrymme",
                  percentage:
                    recipientTotal > 0
                      ? (recipientUsed / recipientTotal) * 100
                      : 0,
                  valueText: `${formatPrice(recipientUsed, 0)} kr`,
                },
                {
                  id: "2",
                  titleKey: "Återstående limit",
                  percentage:
                    recipientTotal > 0
                      ? (recipientRemaining / recipientTotal) * 100
                      : 0,
                  valueText: `${formatPrice(recipientRemaining, 0)} kr`,
                },
              ]}
              colorType="midnight"
              title={`Mottagarlimit - ${formatPrice(recipientTotal, 0)} kr`}
              link="clientcard"
            />
          </Col>
          <Col lg={6} xl={3}>
            <DonutStatSection
              data={[
                {
                  id: "1",
                  titleKey: "Använt utrymme",
                  percentage:
                    repurchaseTotal > 0
                      ? (repurchaseUsed / repurchaseTotal) * 100
                      : 0,
                  valueText: `${formatPrice(repurchaseUsed, 0)} kr`,
                },
                {
                  id: "2",
                  titleKey: "Återstående limit",
                  percentage:
                    repurchaseTotal > 0
                      ? (repurchaseRemaining / repurchaseTotal) * 100
                      : 0,
                  valueText: `${formatPrice(repurchaseRemaining, 0)} kr`,
                },
              ]}
              colorType="ocean"
              title={`Återköpslimit - ${formatPrice(repurchaseTotal, 0)} kr`}
              link="invoices"
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default ClientOverviewBeta;
